import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Form, 
  Input, 
  Button, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Tooltip,
  Modal,
  Badge
} from 'antd';
import { 
  FaRegTrashAlt, 
  FaEdit, 
  FaCheckCircle,
  FaUsers,
  FaSearch,
  FaSync,
  FaUserPlus,
  FaFilter,
  FaEnvelope,
  FaUserCircle,
  FaExclamationTriangle,
  FaBan
} from 'react-icons/fa';
import { HiOutlineUserGroup } from 'react-icons/hi';
import Api from '../api/Api';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteModal from '../modal/delete/DeleteParceiro';
import EditModal from '../modal/alter/AlterParceiro';
import './Parceiro.css';

const { Search } = Input;

const Parceiro = () => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [confirmActiveModal, setConfirmActiveModal] = useState(false);
  const [confirmDeactivateModal, setConfirmDeactivateModal] = useState(false);
  const [partnerToActivate, setPartnerToActivate] = useState(null);
  const [partnerToDeactivate, setPartnerToDeactivate] = useState(null);

  const usuario = JSON.parse(localStorage.getItem('user'));

  const totalPartners = useMemo(() => partners.length, [partners]);
  const activePartners = useMemo(() => 
    partners.filter(p => p.ativo === true).length, 
    [partners]
  );

  const sms_error = (sms) => toast.error(sms, { position: "top-right", autoClose: 3000 });
  const sms_success = (sms) => toast.success(sms, { position: "top-right", autoClose: 2000 });

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const dados = { id: usuario.id };
      const response = await Api.ListUser(dados);
      if (response.success && Array.isArray(response.users)) {
        const formattedPartners = response.users.map((item, index) => ({
          key: item.id,
          id: item.id,
          cliente: item.Nome,
          email: item.email,
          user: item.user,
          ativo: item.partner_status === 1,
          numero: index + 1,
          created_at: item.created_at
        }));
        setPartners(formattedPartners);
        setFilteredPartners(formattedPartners);
      } else {
        sms_error('Erro na resposta ou dados inválidos');
      }
    } catch (error) {
      sms_error('Erro ao buscar parceiros: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      applyFilters(activeFilter);
      return;
    }
    const filtered = partners.filter(item =>
      item.cliente?.toLowerCase().includes(value.toLowerCase()) ||
      item.email?.toLowerCase().includes(value.toLowerCase()) ||
      item.user?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPartners(filtered);
  };

  const applyFilters = (filter) => {
    let filtered = [...partners];
    if (filter === 'active') {
      filtered = filtered.filter(p => p.ativo === true);
    } else if (filter === 'inactive') {
      filtered = filtered.filter(p => p.ativo === false);
    }
    setFilteredPartners(filtered);
  };

  const handleFilter = (filter) => {
    setActiveFilter(filter);
    applyFilters(filter);
  };

  const clearFilters = () => {
    setActiveFilter('all');
    setSearchText('');
    setFilteredPartners(partners);
  };

  // Ativar parceiro
  const handleActive = (id, nome) => {
    setPartnerToActivate({ id, nome });
    setConfirmActiveModal(true);
  };

  const confirmActivate = async () => {
    if (!partnerToActivate) return;
    try {
      const result = await Api.AtiveUser({ id: partnerToActivate.id });
      if (result.success) {
        sms_success('Parceiro ativado com sucesso!');
        fetchPartners();
      } else {
        sms_error(result.message || 'Erro ao ativar parceiro');
      }
    } catch (error) {
      sms_error('Erro ao ativar parceiro: ' + error.message);
    } finally {
      setConfirmActiveModal(false);
      setPartnerToActivate(null);
    }
  };

  // Desativar parceiro
  const handleDeactivate = (id, nome) => {
    setPartnerToDeactivate({ id, nome });
    setConfirmDeactivateModal(true);
  };

  const confirmDeactivate = async () => {
    if (!partnerToDeactivate) return;
    try {
      const result = await Api.DesativarUser({ id: partnerToDeactivate.id });
      if (result.success) {
        sms_success('Parceiro desativado com sucesso!');
        fetchPartners();
      } else {
        sms_error(result.message || 'Erro ao desativar parceiro');
      }
    } catch (error) {
      sms_error('Erro ao desativar parceiro: ' + error.message);
    } finally {
      setConfirmDeactivateModal(false);
      setPartnerToDeactivate(null);
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      if (id === 1 || id === 2) {
        sms_error('Não pode eliminar este parceiro');
      } else {
        const result = await Api.DeleteUser({ id });
        if (result.success) {
          sms_success('Parceiro excluído com sucesso!');
          fetchPartners();
        } else {
          sms_error(result.message || 'Erro ao excluir parceiro');
        }
      }
    } catch (error) {
      sms_error('Erro ao excluir parceiro: ' + error.message);
    }
  };

  const handleEditClient = (record) => {
    setClientToEdit(record);
    editForm.setFieldsValue({ nome: record.cliente, email: record.email, user: record.user, senha: '' });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const result = await Api.AlterUser({ id: clientToEdit.key, ...values });
      if (result.success) {
        sms_success('Parceiro editado com sucesso!');
        setIsEditModalOpen(false);
        fetchPartners();
      } else {
        sms_error(result.message || 'Erro ao editar parceiro');
      }
    } catch (error) {
      sms_error('Erro ao salvar os dados. Verifique os campos e tente novamente.');
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'numero',
      key: 'numero',
      width: 50,
      align: 'center',
      sorter: (a, b) => a.numero - b.numero,
    },
    {
      title: 'Nome',
      dataIndex: 'cliente',
      key: 'cliente',
      render: (text) => (
        <div className="partner-name-cell">
          <FaUserCircle className="partner-icon" />
          <span className="partner-text">{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por nome"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
          <div className="filter-actions">
            <Button type="primary" onClick={confirm} size="small">Filtrar</Button>
            <Button onClick={clearFilters} size="small">Limpar</Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <div className="email-cell">
          <FaEnvelope className="email-icon" />
          <span className="email-text">{email}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="filter-dropdown">
          <Input
            placeholder="Buscar por email"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={confirm}
          />
          <div className="filter-actions">
            <Button type="primary" onClick={confirm} size="small">Filtrar</Button>
            <Button onClick={clearFilters} size="small">Limpar</Button>
          </div>
        </div>
      ),
      filterIcon: () => <FaFilter />,
    },
    {
      title: 'Usuário',
      dataIndex: 'user',
      key: 'user',
      align: 'center',
      render: (user) => <Tag color="blue" className="user-tag">@{user}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      render: (_, record) => (
        <Badge
          status={record.ativo ? 'success' : 'default'}
          text={record.ativo ? 'Ativo' : 'Inativo'}
          className="status-badge"
        />
      ),
      filters: [
        { text: 'Ativo', value: 'active' },
        { text: 'Inativo', value: 'inactive' },
      ],
      onFilter: (value, record) =>
        value === 'active' ? record.ativo === true : record.ativo === false,
    },
    {
      title: 'Ações',
      key: 'acoes',
      align: 'center',
      width: 160,
      render: (_, record) => (
        <Space size={4} wrap>
          {/* Editar */}
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<FaEdit />}
              size="small"
              onClick={() => handleEditClient(record)}
            />
          </Tooltip>

          {/* Eliminar */}
          <Tooltip title="Eliminar">
            <Button
              type="primary"
              danger
              icon={<FaRegTrashAlt />}
              size="small"
              onClick={() => { setClientToDelete(record.key); setIsDeleteModalOpen(true); }}
              disabled={record.id === 1 || record.id === 2}
            />
          </Tooltip>

          {/* Ativar */}
          <Tooltip title="Ativar">
            <Button
              icon={<FaCheckCircle />}
              size="small"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
              onClick={() => handleActive(record.key, record.cliente)}
              disabled={record.ativo === true ||   record.id === 1 || record.id === 2 }
            />
          </Tooltip>

          {/* Desativar */}
          <Tooltip title="Desativar">
            <Button
              icon={<FaBan />}
              size="small"
              style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: 'white' }}
              onClick={() => handleDeactivate(record.key, record.cliente)}
              disabled={record.ativo === false || record.id === 1 || record.id === 2}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="parceiro-container">
      <ToastContainer />

      {/* Cabeçalho */}
      <div className="page-header">
        <h1 className="page-title">
          <HiOutlineUserGroup /> Gestão de Parceiros
        </h1>
        <div className="header-actions">
          <Button icon={<FaSync />} onClick={fetchPartners} loading={loading}>
            Atualizar
          </Button>
          <Button type="primary" icon={<FaUserPlus />}>
            Novo Parceiro
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={8}>
          <Card className="stat-card total-card">
            <Statistic title="Total de Parceiros" value={totalPartners} prefix={<FaUsers />} valueStyle={{ color: '#1890ff' }} />
            <div className="stat-trend"><span>Parceiros registrados</span></div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card active-card">
            <Statistic title="Parceiros Ativos" value={activePartners} prefix={<FaCheckCircle />} valueStyle={{ color: '#52c41a' }} />
            <div className="stat-trend"><span>{totalPartners > 0 ? Math.round((activePartners / totalPartners) * 100) : 0}% do total</span></div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card warning-card">
            <Statistic title="Parceiros Inativos" value={totalPartners - activePartners} prefix={<FaExclamationTriangle />} valueStyle={{ color: '#faad14' }} />
            <div className="stat-trend"><span>Necessitam atenção</span></div>
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="toolbar-card">
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={14} lg={12}>
            <Search
              placeholder="Buscar por nome, email ou usuário..."
              prefix={<FaSearch />}
              enterButton="Buscar"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>

          <Col xs={24} md={10} lg={9}>
            <div className="filter-buttons">
              <label><FaFilter style={{ marginRight: 4 }} />Filtrar:</label>
              <Button type={activeFilter === 'all' ? 'primary' : 'default'} onClick={() => handleFilter('all')}>Todos</Button>
              <Button type={activeFilter === 'active' ? 'primary' : 'default'} onClick={() => handleFilter('active')}>Ativos</Button>
              <Button type={activeFilter === 'inactive' ? 'primary' : 'default'} onClick={() => handleFilter('inactive')}>Inativos</Button>
            </div>
          </Col>

          <Col xs={24} lg={3}>
            <Button onClick={clearFilters} style={{ width: '100%' }}>
              Limpar
            </Button>
          </Col>
        </Row>

        {(searchText || activeFilter !== 'all') && (
          <div className="active-filters">
            <span className="filters-label">Filtros ativos:</span>
            {searchText && <Tag closable onClose={() => handleSearch('')} color="blue">Busca: {searchText}</Tag>}
            {activeFilter === 'active' && <Tag closable onClose={() => handleFilter('all')} color="green">Ativos</Tag>}
            {activeFilter === 'inactive' && <Tag closable onClose={() => handleFilter('all')} color="orange">Inativos</Tag>}
          </div>
        )}
      </Card>

      {/* Tabela */}
      <div className="table-responsive-wrapper">
        <Card className="table-card">
          <Table
            columns={columns}
            dataSource={filteredPartners}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
              pageSizeOptions: ['10', '20', '50'],
              responsive: true,
            }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            locale={{
              emptyText: searchText || activeFilter !== 'all'
                ? 'Nenhum parceiro com os filtros atuais'
                : 'Nenhum parceiro cadastrado',
            }}
          />
        </Card>
      </div>

      {/* Modais */}
      <DeleteModal
        visible={isDeleteModalOpen}
        onOk={() => { handleDeleteClient(clientToDelete); setIsDeleteModalOpen(false); }}
        onCancel={() => setIsDeleteModalOpen(false)}
      />

      <EditModal
        visible={isEditModalOpen}
        onOk={handleSaveEdit}
        onCancel={() => setIsEditModalOpen(false)}
        form={editForm}
      />

      {/* Modal — Confirmar Ativação */}
      <Modal
        title="Confirmar Ativação"
        open={confirmActiveModal}
        onOk={confirmActivate}
        onCancel={() => { setConfirmActiveModal(false); setPartnerToActivate(null); }}
        okText="Sim, ativar"
        cancelText="Cancelar"
      >
        <div className="confirm-modal-content">
          <div className="confirm-icon confirm-icon--success">✅</div>
          <p>Tem certeza que deseja ativar o parceiro <strong>{partnerToActivate?.nome}</strong>?</p>
          <p className="modal-note modal-note--success">O parceiro terá acesso ao sistema após a ativação.</p>
        </div>
      </Modal>

      {/* Modal — Confirmar Desativação */}
      <Modal
        title="Confirmar Desativação"
        open={confirmDeactivateModal}
        onOk={confirmDeactivate}
        onCancel={() => { setConfirmDeactivateModal(false); setPartnerToDeactivate(null); }}
        okText="Sim, desativar"
        okButtonProps={{ danger: true }}
        cancelText="Cancelar"
      >
        <div className="confirm-modal-content">
          <div className="confirm-icon confirm-icon--danger">🚫</div>
          <p>Tem certeza que deseja desativar o parceiro <strong>{partnerToDeactivate?.nome}</strong>?</p>
          <p className="modal-note modal-note--danger">O parceiro perderá o acesso ao sistema.</p>
        </div>
      </Modal>
    </div>
  );
};

export default Parceiro;